import { useState, useEffect, useCallback } from 'react';
import { Playbook, fetchPlaybook } from '../utils/web-api';
import { useAuthHeader } from "../context/AuthContext";
import { useAnalytics } from '../context/AnalyticsContext';
import { toastError } from '../utils/toast';

interface UsePlaybookReturn {
  playbook: Playbook | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export function usePlaybook(playbookId: string | undefined): UsePlaybookReturn {
  const [playbook, setPlaybook] = useState<Playbook | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const authHeader = useAuthHeader();
  const { sendError } = useAnalytics();

  const reload = useCallback(() => {
    setReloadTrigger(prev => prev + 1);
  }, []);

  useEffect(() => {
    if (!playbookId) {
      setPlaybook(null);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;

    const loadPlaybook = async () => {
      if (!authHeader) {
        if (!cancelled) {
          setPlaybook(null);
          setError(null);
          setLoading(false);
        }
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const data = await fetchPlaybook(authHeader, playbookId);
        if (!cancelled) {
          setPlaybook(data);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load playbook:', err);
          sendError(err as Error, {
            component: 'usePlaybook',
            action: 'load_playbook',
            playbookId: playbookId
          });
          setError('Failed to load playbook');
          toastError('Failed to load playbook');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadPlaybook();

    return () => {
      cancelled = true;
    };
  }, [playbookId, authHeader, sendError, reloadTrigger]);

  return { playbook, loading, error, reload };
}

